import { useEffect, useState } from 'react';

import { useDispatch, useSelector } from 'react-redux';

import Configure from 'assets/unifiedCommunication/Configure.svg';
import MobileList from 'common-ui/mobileList';
import Switch from 'common-ui/switch';
import { Table } from 'common-ui/table';
import {
  getAllPermissionsByAccessType,
  togglePermissionStatus,
} from 'features/chat/chatActions';
import { selectAllRolePermissions } from 'features/chat/chatSlice';
import { getRoles } from 'features/users/usersActions';
import { selectRoles } from 'features/users/usersSlice';
import Tag from 'pages/components/tag';

import ChannelConfigurator from '../components/ChannelConfigurator';
import PlatformIcons from '../components/PlatformIcons';
import { RowIcon } from '../components/rowIcon';
import {
  ConfigureButton,
  ConfigureIcon,
  MobileExpandedWrapper,
  MobileLabel,
  MobileRow,
  StatusText,
  StatusWrapper,
} from './ByRole.styles';

const ByRole = () => {
  const dispatch = useDispatch();
  const [newData, setNewData] = useState([]);

  const roles = useSelector(selectRoles);
  const allRolePermissions = useSelector(selectAllRolePermissions);

  const [isChannelModalOpen, setIsChannelModalOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [channelPermissions, setChannelPermissions] = useState({});

  const getPermissionRecord = (roleName) => {
    return allRolePermissions.find((perm) => perm.targetId === roleName);
  };

  const getStatus = (roleName) => {
    const permRecord = getPermissionRecord(roleName);
    if (!permRecord) return 'Disabled';
    return permRecord.isActive ? 'Active' : 'Disabled';
  };

  const handleToggleStatus = (roleName) => {
    const permRecord = getPermissionRecord(roleName);
    if (!permRecord) return;

    dispatch(
      togglePermissionStatus({
        permissionId: permRecord._id,
        isActive: !permRecord.isActive,
        accessType: 'role',
      })
    );
  };

  const parsePermissions = (roleName) => {
    const viewPlatforms = [];
    const replyPlatforms = [];

    const rolePermission = allRolePermissions.find((perm) => perm.targetId === roleName);

    if (rolePermission?.permissions) {
      rolePermission.permissions.forEach((perm) => {
        if (!perm.isDenied) {
          if (perm.type === 'view_only') {
            viewPlatforms.push(perm.platform);
          } else if (perm.type === 'view_and_answer') {
            replyPlatforms.push(perm.platform);
          }
        }
      });
    }

    return { viewPlatforms, replyPlatforms };
  };

  const colors = [
    { bg: 'linear-gradient(135deg, #D0FAE5 0%, #A4F4CF 100%)', color: '#15C7A7' },
    { bg: 'linear-gradient(135deg, #F1F5F9 0%, #E2E8F0 100%)', color: '#62748E' },
    { bg: 'linear-gradient(135deg, #FEF3C6 0%, #FEE685 100%)', color: 'white' },
    { bg: 'linear-gradient(135deg, #2A76E9 0%, #20457B 100%)', color: 'white' },
  ];

  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const mobileColumns = [
    {
      key: 'role',
      title: 'Role',
      dataIndex: 'role',
      render: (value, row, index) => {
        const isSpecialRole = value === 'Super Admin' || value === 'General Manager';

        return (
          <RowIcon
            color={isSpecialRole ? colors[2] : index % 2 === 0 ? colors[0] : colors[1]}
            value={value}
          />
        );
      },
    },
    {
      key: 'status',
      title: 'Status',
      dataIndex: 'role',
      render: (roleName) => {
        const status = getStatus(roleName);
        const isActive = status === 'Active';
        const hasPermission = !!getPermissionRecord(roleName);

        return (
          <StatusWrapper $isActive={isActive}>
            <StatusText $isActive={isActive}>{status}</StatusText>
            <Switch
              activeColor="#15C7A7"
              isOn={isActive}
              onToggle={() => handleToggleStatus(roleName)}
              disabled={!hasPermission}
            />
          </StatusWrapper>
        );
      },
    },
  ];

  const renderExpandable = (row) => {
    const { viewPlatforms, replyPlatforms } = parsePermissions(row.role);

    return (
      <MobileExpandedWrapper>
        <MobileRow>
          <MobileLabel>View</MobileLabel>
          <PlatformIcons platforms={viewPlatforms} />
        </MobileRow>

        <MobileRow>
          <MobileLabel>View and Reply</MobileLabel>
          <PlatformIcons platforms={replyPlatforms} />
        </MobileRow>

        <ConfigureButton
          onClick={() => {
            setChannelPermissions({});
            setSelectedItem(row);
            setIsChannelModalOpen(true);
          }}
        >
          <ConfigureIcon src={Configure} />
          Configure
        </ConfigureButton>
      </MobileExpandedWrapper>
    );
  };

  const columns = [
    {
      key: 'role',
      title: 'Role Name',
      dataIndex: 'role',
      render: (value, row, index) => {
        const isSpecialRole = value === 'Super Admin' || value === 'General Manager';
        return (
          <RowIcon
            color={isSpecialRole ? colors[2] : index % 2 === 0 ? colors[0] : colors[1]}
            value={value}
          />
        );
      },
    },
    {
      key: 'viewAccess',
      title: 'View',
      dataIndex: 'role',
      render: (roleName) => {
        const { viewPlatforms } = parsePermissions(roleName);
        return <PlatformIcons platforms={viewPlatforms} />;
      },
    },
    {
      key: 'viewReply',
      title: 'View and Reply',
      dataIndex: 'role',
      render: (roleName) => {
        const { replyPlatforms } = parsePermissions(roleName);
        return <PlatformIcons platforms={replyPlatforms} />;
      },
    },
    {
      key: 'channels',
      title: 'Channels',
      dataIndex: 'channels',
      render: (_, row) => (
        <ConfigureButton
          onClick={() => {
            setChannelPermissions({});
            setSelectedItem(row);
            setIsChannelModalOpen(true);
          }}
        >
          <ConfigureIcon src={Configure} alt="configure" />
          Configure
        </ConfigureButton>
      ),
    },
    {
      key: 'status',
      title: 'Status',
      dataIndex: 'role',
      render: (roleName) => {
        const isSpecialRole = roleName === 'Super Admin' || roleName === 'General Manager';
        const status = getStatus(roleName);
        const isActive = status === 'Active';
        const hasPermission = !!getPermissionRecord(roleName);

        if (isSpecialRole) return <Tag type="statuses" variant={status} />;

        return (
          <StatusWrapper $isActive={isActive}>
            <StatusText $isActive={isActive}>{status}</StatusText>
            <Switch
              activeColor="#15C7A7"
              isOn={isActive}
              onToggle={() => handleToggleStatus(roleName)}
              disabled={!hasPermission}
            />
          </StatusWrapper>
        );
      },
    },
  ];

  // Fetch roles
  useEffect(() => {
    if (roles.length === 0) {
      dispatch(getRoles({}));
    }
  }, []);

  // Format roles data
  useEffect(() => {
    if (roles && roles.length > 0) {
      const formatted = roles.map((role) => ({
        id: role.uuid,
        role: role.name,
        status: 'Active',
      }));

      // Add Super Admin at the top if not already present
      const hasSuperAdmin = formatted.some((r) => r.role === 'Super Admin');
      if (!hasSuperAdmin) {
        formatted.unshift({
          id: 'super-admin',
          role: 'Super Admin',
          status: 'Active',
        });
      }

      formatted.sort((a, b) => {
        if (a.role === 'Super Admin') return -1;
        if (b.role === 'Super Admin') return 1;
        if (a.role === 'General Manager') return -1;
        if (b.role === 'General Manager') return 1;
        return 0;
      });

      setNewData(formatted);
    }
  }, [roles]);

  // Fetch all role permissions when navigating to this page
  useEffect(() => {
    dispatch(getAllPermissionsByAccessType({ accessType: 'role' }));
  }, [dispatch]);

  // Refetch all permissions when modal closes to update the table
  const handleModalClose = () => {
    setIsChannelModalOpen(false);
    dispatch(getAllPermissionsByAccessType({ accessType: 'role' }));
  };

  return (
    <>
      {isMobile ? (
        <MobileList
          columns={mobileColumns}
          data={newData}
          expandable={renderExpandable}
          loading={false}
          pagination={false}
        />
      ) : (
        <Table data={newData} columns={columns} pagination={false} />
      )}

      {selectedItem && (
        <ChannelConfigurator
          isOpen={isChannelModalOpen}
          onClose={handleModalClose}
          role={selectedItem}
          channelPermissions={channelPermissions}
          setChannelPermissions={setChannelPermissions}
        />
      )}
    </>
  );
};

export default ByRole;
